import React, { useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'

// TODO(IGN-393): The user-persistence logic below (mergeUserData + localUserData
// + updateUser + mergedUser useMemo) and the nested-provider merge are an
// adc-exact copy of @adc/ui-adc's ApplicationContextProvider
// (packages/adc/src/components/auctionApplication/context.tsx). IGN-393 tracks
// extracting this shared logic into @adc/ui-core so both shells consume one
// implementation. Keep this in sync with the adc context until then.
// https://linear.app/auctioncom/issue/IGN-393

// TODO: Can we enforce this type or something?
const ApplicationContextShape = {
  // Global indicator for whether async work is currently happening
  isCurrentlyFetching: false,

  // Global user session. This should be serialized from the backend
  user: {},

  // Shared graph websocket instance
  graphWebsocket: undefined,
}

// TODO: Should I export this? Can't see a need for it directly now. And I export the consumer and provider down below
// to do the same thing.
const ApplicationContext = React.createContext({})
export const ApplicationContextConsumer = ApplicationContext.Consumer

/**
 * Merge a partial update into user data. Arrays are replaced wholesale (not
 * merged by index, which is lodash merge's default) so a local override can
 * shrink a list — e.g. removing a buying-preferences location. Objects still
 * deep-merge, preserving untouched keys.
 *
 * TODO(IGN-393): adc-exact copy of @adc/ui-adc's mergeUserData. Extract to
 * @adc/ui-core. https://linear.app/auctioncom/issue/IGN-393
 */
export const mergeUserData = (base, update) =>
  _.mergeWith({}, base, update, (_objValue, srcValue) =>
    Array.isArray(srcValue) ? srcValue : undefined
  )

export const ApplicationContextProvider = props => {
  const { children } = props

  // Access the current context value to support nested providers. When an ApplicationContextProvider is nested inside
  // another one, this allows the child provider to inherit values from the parent provider that weren't explicitly
  // passed as props. This enables composition patterns where parent providers can set baseline values and child
  // providers can override only specific properties. In our shell setup, the top-level provider (in the app's
  // client/index.js) owns graphWebsocket, while the shell-level provider owns the reactive user.
  const currentContext = useContext(ApplicationContext)

  // Build provider values with proper fallback priority for each key:
  // 1. propsValue - Explicit props passed to this provider (highest priority)
  // 2. currentContextValue - Values from a parent provider if one exists (middle priority)
  // 3. defaultContextValue - Default values from ApplicationContextShape (lowest priority)
  const { user, graphWebsocket } = Object.keys(ApplicationContextShape).reduce(
    (acc, key) => {
      const propsValue = props[key]
      const currentContextValue = currentContext[key]
      const defaultContextValue = ApplicationContextShape[key]

      // NOTE: If we ever explicitly want null passed in here we would need to change this condition to check for
      // `undefined`.
      acc[key] =
        [propsValue, currentContextValue].find(v => !_.isNil(v)) ||
        defaultContextValue

      return acc
    },
    {}
  )

  const [isCurrentlyFetching, setIsFetching] = useState(false)

  const [localUserData, setLocalUserData] = useState({})

  const updateUser = updatedData => {
    setLocalUserData(currentData => mergeUserData(currentData, updatedData))
  }

  // Memoize user object to prevent unnecessary re-renders in consuming components from the object reference changing.
  // Derived directly from the user prop (not frozen in useState), so it stays reactive to prop changes after login.
  const mergedUser = useMemo(
    () => mergeUserData(user, localUserData),
    [user, localUserData]
  )

  const providerValues = {
    isCurrentlyFetching,
    setIsFetching,
    user: mergedUser,
    graphWebsocket,
    actions: {
      updateUser,
    },
  }

  return (
    <ApplicationContext.Provider value={providerValues}>
      {children}
    </ApplicationContext.Provider>
  )
}

ApplicationContextProvider.propTypes = {
  // user and graphWebsocket are consumed dynamically via the reduce over
  // ApplicationContextShape above, which react/no-unused-prop-types can't
  // statically detect.
  // eslint-disable-next-line react/no-unused-prop-types
  user: PropTypes.object,
  // eslint-disable-next-line react/no-unused-prop-types
  graphWebsocket: PropTypes.object,
  children: PropTypes.any,
}

// For use with function components using hooks
export const useApplicationContext = () => useContext(ApplicationContext)

// A lot of our components are not in a state where we can drop in hooks, so defining this
// HoC here for use with existing code.
export const withApplicationContext = Component => props =>
  (
    <ApplicationContextConsumer>
      {context => <Component {...context} {...props} />}
    </ApplicationContextConsumer>
  )
